<div class="meeting-page-container">
  @if (isRecording()) {
    <div class="recording-indicator">
      <mat-icon>screen_record</mat-icon>
      <span>{{ 'VideoCall.Recording' | translate }}</span>
    </div>
  }
  <!-- STATE 1: Pre-call / Joining Screen -->
  @if (!isMeetingActive() && waitingStatus() === 'none') {
    <div class="join-call-container">
      <div class="join-call-wrapper">
        <app-join-call-settings
          [username]="username()"
          (usernameChange)="username.set($event)"
          [email]="email()"
          (emailChange)="email.set($event)"
          [isGuest]="isGuest()"
          (joinCall)="onJoinCall()"
          [loading]="loading()"
          [callID]="callID()!"
        />
        <a class="mt-4" (click)="goHome()">{{ 'VideoCall.GoToHomePage' | translate }}</a>
        @if (error()) {
          <div class="error-message">{{ error() | translate }}</div>
        }
        <a class="powered-by" href="https://skillztank.com" target="_blank" rel="noopener noreferrer">
          {{ 'PublicDisplayer.PoweredBy' | translate }}
          <span class="powered-by__brand">Skillztank</span>
        </a>
      </div>
    </div>
  }

  <!-- STATE 1.5: Waiting Room Screen -->
  @if (!isMeetingActive() && waitingStatus() === 'waiting') {
    <div class="join-call-container">
      <div class="waiting-room-card">
        <div class="waiting-room-icon">
          <mat-spinner diameter="24" color="primary"></mat-spinner>
        </div>
        <h2 class="waiting-room-title">{{ 'VideoCall.WaitingRoom.Title' | translate }}</h2>
        <p class="waiting-room-message">{{ 'VideoCall.WaitingRoom.Message' | translate }}</p>
        <button mat-button color="warn" (click)="onLeaveWaitingRoom()">{{ 'VideoCall.WaitingRoom.Cancel' | translate }}</button>
      </div>
    </div>
  }

  <!-- STATE 2: In-call Screen -->
  @if (isMeetingActive()) {
    <div class="in-call-layout">
      <!-- New: Main layout container for the stage and side panel -->
      <div class="main-layout-container">
        <!-- New: The main "stage" that holds all video/screen share content -->
        <!-- It dynamically changes layout based on sharing and side panel states -->
        <div class="stage-container" [class.is-sharing]="isScreenSharing() || whiteboardOpened()" [class.side-panel-open]="isSidePanelOpen()">
          <!-- Layout 1: Whiteboard View -->
          @if (whiteboardOpened()) {
            <!-- The Whiteboard itself -->
            <div class="whiteboard-view">
              <room-whiteboard style="width: 100%; height: 100%; display: block;" />
            </div>

            <!-- The participant bar (sidebar or top-bar) -->
            <div class="participant-bar">
              @for (p of allParticipants(); track p.attendee.chimeAttendeeId) {
                <div class="video-cell video-cell-participant-bar" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
                  @if (p.tileState; as tile) {
                    <app-video-tile [tileState]="tile" [isLocal]="p.isLocal" />
                  } @else {
                    <div class="no-video-placeholder">
                      <mat-icon>person</mat-icon>
                    </div>
                  }
                  <div class="participant-name">{{ p.isLocal ? ('You' | translate) : p.attendee.username }}</div>
                </div>
              }
            </div>
          }

          <!-- Layout 2: Presentation View (Screen Share Active) -->
          @else if (isScreenSharing()) {
            <!-- The Screen Share itself -->
            @if (screenShareTile(); as tile) {
              <div class="screen-share-view">
                <app-video-tile [tileState]="tile" [enableZoom]="true" />
              </div>
            }

            <!-- New: The participant bar (sidebar or top-bar) -->
            <div class="participant-bar">
              @for (p of allParticipants(); track p.attendee.chimeAttendeeId) {
                <div class="video-cell video-cell-participant-bar" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
                  @if (p.tileState; as tile) {
                    <app-video-tile [tileState]="tile" [isLocal]="p.isLocal" />
                  } @else {
                    <div class="no-video-placeholder">
                      <mat-icon>person</mat-icon>
                    </div>
                  }
                  <div class="participant-name">{{ p.isLocal ? ('You' | translate) : p.attendee.username }}</div>
                </div>
              }
            </div>
          }

          <!-- Layout 3: Gallery View (No Screen Share) -->
          @else {
            <div class="video-grid-gallery">
              <!-- Remote Participants' Videos (or All on mobile) -->
              @for (p of (isMobileOrTablet() ? allParticipants() : remoteParticipants()); track p.attendee.chimeAttendeeId) {
                <div class="video-cell" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
                  @if (p.tileState; as tile) {
                    <app-video-tile [tileState]="tile" [isLocal]="p.isLocal" />
                  } @else {
                    <div class="no-video-placeholder">
                      <mat-icon>person</mat-icon>
                    </div>
                  }
                  <div class="participant-name">{{ p.isLocal ? ('You' | translate) : p.attendee.username }}</div>
                </div>
              }
            </div>

            <!-- Local User's Video (as Picture-in-Picture) - Hidden on Mobile -->
            @if (!isMobileOrTablet() && localParticipant(); as p) {
              <div class="video-cell local-video-pip" [class.is-speaking]="p.attendee.chimeAttendeeId === activeSpeakerId()">
                @if (p.tileState; as tile) {
                  <app-video-tile [tileState]="tile" [isLocal]="true" />
                } @else {
                  <div class="no-video-placeholder">
                    <mat-icon>person</mat-icon>
                  </div>
                }
                <div class="participant-name">{{ 'You' | translate }}</div>
              </div>
            }
          }
        </div>

        <!-- New: Main Side Panel for Chat or Participant List -->
        @if (isSidePanelOpen()) {
          <div class="side-panel-container">
            @if (chatOpen()) {
              <app-chat-box (closeChat)="chatOpen.set(false)" />
            }
            @if (participantsOpen()) {
              <app-participant-list
                [roster]="chimeService.allParticipants()"
                [isHost]="isHost()"
                [localUserId]="localUserId()"
                [isAudioLocked]="chimeService.isAudioLocked()"
                [isCameraLocked]="chimeService.isCameraLocked()"
                [isScreenShareLocked]="chimeService.isScreenShareLocked()"
                [isChatLocked]="chimeService.isChatLocked()"
                [activeSpeakerId]="chimeService.activeSpeakerId()"
                [hostAttendeeIds]="chimeService.hostAttendeeIds()"
                [waitingParticipants]="waitingParticipants()"
                (muteParticipant)="chimeService.muteParticipant($event)"
                (stopVideoParticipant)="chimeService.stopVideoParticipant($event)"
                (kickParticipant)="chimeService.kickParticipant($event)"
                (promoteParticipant)="chimeService.promoteParticipant($event)"
                (muteAll)="chimeService.muteAllParticipants()"
                (toggleLockAudio)="chimeService.toggleLockAudio()"
                (toggleLockCamera)="chimeService.toggleLockCamera()"
                (toggleLockScreenShare)="chimeService.toggleLockScreenShare()"
                (toggleLockChat)="chimeService.toggleLockChat()"
                (closePanel)="participantsOpen.set(false)"
                (admitParticipant)="onAdmitParticipant($event)"
                (rejectParticipant)="onRejectParticipant($event)"
              />
            }
          </div>
        }
      </div>

      <!-- Call Controls (no change, but note updated toggle methods) -->
      <app-call-controls
        [isDrawer]="false"
        [micOn]="!chimeService.isMuted()"
        [cameraActive]="chimeService.isCameraEnabled()"
        [chatActive]="chatOpen()"
        [isScreenSharing]="isScreenSharing()"
        [participantCount]="chimeService.roster().length"
        [waitingCount]="waitingParticipants().length"
        [isVideoFxActive]="isVideoFxActive()"
        [callID]="callID()"
        [isHost]="isHost()"
        (toggleMic)="chimeService.toggleMicrophone()"
        (toggleCamera)="chimeService.toggleCamera()"
        (toggleChat)="onToggleChat()"
        (toggleScreenShare)="onToggleScreenShare($event)"
        (leaveCall)="onLeaveCall()"
        (toggleParticipants)="onToggleParticipants()"
        (toggleVideoFx)="onToggleVideoFx()"
      />
      @if (isMeetingActive() && isHost() && !chatOpen()) {
        <div class="meeting-duration-overlay">
          <div class="meeting-duration-box">
            <mat-icon>schedule</mat-icon>
            <span>{{ chimeService.meetingDuration() }}</span>
          </div>
        </div>
      }
    </div>
  }
</div>
